import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { externalLinks } from 'src/externalLinks';
import { Assessment } from 'src/models/assessment/_types';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { AssessmentFormValues } from '../AssessmentNewPage';

import { PreviewDialog } from './PreviewDialog';

interface AssessmentCustomizeProps {
  useTemplate: boolean;
  setUseTemplate: (useTemplate: boolean) => void;
  useResults: boolean;
  setUseResults: (useResults: boolean) => void;
  useThankYou: boolean;
  setUseThankYou: (useThankYou: boolean) => void;
}

export const AssessmentCustomize = ({
  useTemplate,
  setUseTemplate,
  useResults,
  setUseResults,
  useThankYou,
  setUseThankYou,
}: AssessmentCustomizeProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.new.customizeTab',
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isResultsPreview, setIsResultsPreview] = useState(false);
  const { values } = useFormikContext<AssessmentFormValues>();

  const handleModalPreview = (results: boolean) => {
    setIsResultsPreview(results);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  return (
    <Stack spacing={1}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="buttonText"
            label={t('buttonText')}
            placeholder={t('buttonTextPlaceholder')}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="customCss"
            label={t('customCss')}
            placeholder={t('customCssPlaceholder')}
            multiline
            rows={5}
            fullWidth
          />
        </Grid>
      </Grid>
      {useTemplate ? (
        <>
          <Typography variant="h6">{t('customTemplate')}</Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <HtmlTypography>{t('customTemplateWarning')}</HtmlTypography>
          </Alert>
          <FormTextField
            name="customTemplate"
            label={t('customizeTemplateLabel')}
            multiline
            rows={10}
            fullWidth
          />
          <Box>
            <Button
              variant="borderless"
              onClick={() => handleModalPreview(false)}
            >
              {t('preview')}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Button variant="borderless" onClick={() => setUseTemplate(true)}>
            {t('showCustomAssessmentEditor')}
          </Button>
        </Box>
      )}
      {useResults ? (
        <>
          <Typography variant="h6">{t('customResultsPage')}</Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <HtmlTypography>{t('customTemplateWarning')}</HtmlTypography>
          </Alert>
          <FormTextField
            name="customResults"
            label={t('customizeResultsLabel')}
            multiline
            rows={10}
            fullWidth
          />
          <Box>
            <Button
              variant="borderless"
              onClick={() => handleModalPreview(true)}
            >
              {t('preview')}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Button variant="borderless" onClick={() => setUseResults(true)}>
            {t('showCustomResultsEditor')}
          </Button>
        </Box>
      )}
      {useThankYou ? (
        <>
          <Typography variant="h6">{t('customThankYouPage')}</Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <HtmlTypography>{t('customThankYouWarning')}</HtmlTypography>
          </Alert>
          <FormTextField
            name="thankYouPage"
            label={t('customizeThankYouLabel')}
            multiline
            rows={10}
            fullWidth
          />
          <Box>
            <Button
              variant="borderless"
              href={externalLinks.v2Url(
                `/assessment/${values.id || ''}?preview=true`
              )}
              target="_blank"
              component="a"
            >
              {t('preview')}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Button variant="borderless" onClick={() => setUseThankYou(true)}>
            {t('showCustomThankYouEditor')}
          </Button>
        </Box>
      )}
      <PreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        isResults={isResultsPreview}
        assessment={
          {
            ...values,
            id: values.id || 'preview',
            fields: values.fields || [],
          } as Assessment
        }
      />
    </Stack>
  );
};
